import { useLayoutEffect, useRef, useState } from 'react';

/**
 * A CSS height that makes a panel run from wherever it sits down to the bottom of the screen.
 *
 * WHY THIS EXISTS. Full-height panels — the messaging view, chiefly — were sized by subtracting
 * a hand-counted number of pixels from the viewport: `calc(100dvh - 148px)`. That number is only
 * ever right for the one layout it was counted against. Every header above the panel that wraps
 * onto a second line on a phone, every banner (trial, hearing alert) that appears, pushes the
 * panel down without changing the number, so the bottom of it ends up below the fold: on the
 * Communications page the conversation list ran off the bottom of the screen with the end of it
 * unreachable.
 *
 * So measure instead of counting. `top` is the panel's own distance from the top of the
 * document, which is whatever the headers above it actually came to today.
 *
 * `dvh`, not `vh`, and the subtraction stays in CSS: on a phone `100vh` is the height the screen
 * would have with the browser's address bar hidden, which is not the height you can see. `dvh`
 * tracks the visible area as the bar collapses, and leaving the arithmetic to CSS means it keeps
 * tracking it without re-measuring.
 */
export function useFillViewportHeight(gapPx = 24) {
  const ref = useRef<HTMLDivElement>(null);
  /** Until the first measurement there is nothing sensible to say; the caller's default stands. */
  const [height, setHeight] = useState<string | undefined>(undefined);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    const measure = () => {
      // Document-relative, so a page that happens to be scrolled measures the same as one at
      // the top. `scrollY` is 0 on arrival, which is when this first runs.
      const top = el.getBoundingClientRect().top + window.scrollY;
      // `max()` is a floor against collapse, not a layout floor: at 220px it is far below the
      // room any real header leaves, so it only ever bites if the content above is taller than
      // the screen — where the alternative is a panel of zero height.
      setHeight(`max(220px, calc(100dvh - ${Math.round(top + gapPx)}px))`);
    };

    measure();

    // Catches the header above wrapping or unwrapping, a banner appearing, fonts landing — all
    // the things that move the panel without a window resize. Re-measuring produces the same
    // string when nothing moved, and React stops there, so this settles rather than loops.
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    observer?.observe(document.body);
    window.addEventListener('resize', measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [gapPx]);

  return [ref, height] as const;
}
